import { useState, useEffect } from 'react';
import { Profile, ProfileStatus } from '../../types';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';

export const AdminUsers = () => {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [resetForm, setResetForm] = useState<{ userId: string; password: string; confirm: string } | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setUsers((data ?? []) as Profile[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminReset = async (userId: string, password: string) => {
    setMessage(null);
    try {
      const { data, error: fnError } = await (supabase.rpc as any)('admin_reset_password', {
        target_user_id: userId,
        new_password: password,
      });
      if (fnError) throw fnError;
      if (!data) throw new Error('Failed to reset password');
      setMessage({ text: 'Password reset successfully.', type: 'success' });
      setResetForm(null);
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Failed to reset password', type: 'error' });
    }
  };

  const handleStatusChange = async (id: string, status: ProfileStatus) => {
    setMessage(null);
    try {
      const { error: updateError } = await supabase.from('profiles').update({ status }).eq('id', id);
      if (updateError) throw updateError;
      setMessage({ text: 'Status updated.', type: 'success' });
      loadUsers();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Failed to update status', type: 'error' });
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  if (loading) return <Loading full />;
  if (error) return <ErrorAlert message={error} onRetry={loadUsers} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <p className="text-sm text-gray-600">All registered portal users.</p>
        {message && (
          <div className={`mt-2 rounded p-3 text-sm ${message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
            {message.text}
          </div>
        )}
      </div>
      {users.length === 0 ? (
        <Empty title="No users" message="No users have signed up yet." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Joined</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user: Profile) => (
                <tr key={user.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.full_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.email ?? user.phone ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{user.role}</td>
                  <td className="px-4 py-3 text-sm">
                    <select
                      value={user.status}
                      onChange={(e) => handleStatusChange(user.id, e.target.value as ProfileStatus)}
                      className="rounded border border-gray-300 px-2 py-1 text-xs"
                    >
                      <option value="UNDER_REVIEW">Under Review</option>
                      <option value="APPROVED">Approved</option>
                      <option value="BLOCKED">Blocked</option>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(user.created_at).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3 text-sm">
                    {resetForm?.userId === user.id ? (
                      <div className="space-y-2">
                        <input
                          type="password"
                          placeholder="New password"
                          value={resetForm.password}
                          onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                          className="w-32 rounded border border-gray-300 px-2 py-1 text-xs"
                        />
                        <input
                          type="password"
                          placeholder="Confirm"
                          value={resetForm.confirm}
                          onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                          className="w-32 rounded border border-gray-300 px-2 py-1 text-xs"
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              if (resetForm.password !== resetForm.confirm) {
                                setMessage({ text: 'Passwords do not match', type: 'error' });
                                return;
                              }
                              if (resetForm.password.length < 6) {
                                setMessage({ text: 'Password must be at least 6 characters', type: 'error' });
                                return;
                              }
                              handleAdminReset(user.id, resetForm.password);
                            }}
                            className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                          >
                            Set
                          </button>
                          <button
                            onClick={() => setResetForm(null)}
                            className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setResetForm({ userId: user.id, password: '', confirm: '' })}
                        className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        Reset Password
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
